import { Helmet, HelmetData } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../../utils/animations";
import {
  AI_BG_NEW,
  Capital_Build,
  Capital_Deliver,
  Capital_Market_Hero,
  Clarity_Icon,
  Context_Aware2_Icon,
  Our_Product_Bg,
  Security_Privacy_Icon,
  Transparency_Icon,
} from "../../../assets";
const helmetData = new HelmetData({});

const why = [
  {
    icon: Clarity_Icon,
    title: "Low‑Latency Aware",
    text: "Built to work alongside HFT, not compete with it.",
  },
  {
    icon: Context_Aware2_Icon,
    title: "Explainable by Default",
    text: "Every AI insight is sourced, reasoned and auditable",
  },
  {
    icon: Transparency_Icon,
    title: "IP‑Safe",
    text: "No model weight or prompt leakage to public infrastructure",
  },
  {
    icon: Security_Privacy_Icon,
    title: "Compliant",
    text: "Aligned with trading surveillance and supervisory requirements",
  },
];

export default function CapitalMarketsPage() {
  const pageRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        maskedLineReveal(pageRef.current.querySelector(".cm-hero-heading"), {
          scrollTrigger: revealTrigger(".cm-hero-heading"),
        });
        riseFrom(".cm-hero-copy", {
          delay: 0.2,
          scrollTrigger: revealTrigger(".cm-hero-copy", "top 85%"),
        });
        riseFrom(".cm-hero-media", {
          y: 40,
          duration: 1.1,
          delay: 0.3,
          scrollTrigger: revealTrigger(".cm-hero-media", "top 85%"),
        });
        riseFrom(".cm-what-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".cm-what-row", "top 75%"),
        });
        riseFrom(".cm-what-copy > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".cm-what-row", "top 75%"),
        });
        riseFrom(".cm-why-head", {
          scrollTrigger: revealTrigger(".cm-why-head"),
        });
        riseFrom(".cm-why-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".cm-why-grid", "top 75%"),
        });
        riseFrom(".cm-build-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".cm-build-row", "top 75%"),
        });
        riseFrom(".cm-build-copy > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".cm-build-row", "top 75%"),
        });
        riseFrom(".cm-cta-item", {
          stagger: 0.12,
          scrollTrigger: revealTrigger(".cm-cta", "top 85%"),
        });
      });
      return () => mm.revert();
    },
    { scope: pageRef }
  );

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>AI for Capital Markets | Analytics Intelligence Ltd — Secure Market Data &amp; AI Intelligence</title>
        <link rel="canonical" href="https://analyticsintelligence.com/solutions/capital-markets" />
        <meta
          name="description"
          content="Analytics Intelligence Ltd delivers AI infrastructure for capital markets — secure data pipelines, LLM-driven analytics, real-time market intelligence, and financial automation for banks and trading institutions in Africa and globally."
        />
        <meta
          name="keywords"
          content="Analytics Intelligence, Analytics Intelligence Ltd, Analytics Intelligence Banking, AI solutions for capital markets, AI for banking, machine learning for banks, AI infrastructure for banks in Africa, predictive analytics for financial services, AI fraud detection platform Africa, regulatory reporting automation with AI, data governance solutions for banks, AI compliance for financial institutions, enterprise analytics platform, secure AI infrastructure, LLM workflows, real-time market intelligence, financial analytics, AI company in Mauritius, banking AI SSA"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta property="og:title" content="AI for Capital Markets | Analytics Intelligence Ltd" />
        <meta property="og:description" content="Secure AI infrastructure for capital markets — LLM analytics, real-time market intelligence, and financial automation by Analytics Intelligence Ltd." />
        <meta property="og:url" content="https://analyticsintelligence.com/solutions/capital-markets" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://analyticsintelligence.com/favicon.png" />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta name="twitter:title" content="AI for Capital Markets | Analytics Intelligence Ltd" />
        <meta name="twitter:description" content="Secure AI infrastructure for capital markets — LLM analytics, real-time market intelligence, and financial automation by Analytics Intelligence Ltd." />
        <meta name="twitter:image" content="https://analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "AI Solutions for Capital Markets",
            "serviceType": "AI Infrastructure & Analytics for Capital Markets",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            },
            "description": "Analytics Intelligence Ltd provides secure AI infrastructure, LLM-driven analytics, and real-time market intelligence for capital markets, trading institutions, and financial organisations in Africa and globally.",
            "areaServed": ["Mauritius", "Africa", "United Kingdom", "Europe", "North America", "South America", "Australia"],
            "url": "https://analyticsintelligence.com/solutions/capital-markets"
          }
        `}</script>
      </Helmet>

      <main ref={pageRef}>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[90vh] flex items-center"
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16  pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex basis-3/5 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-4 flex flex-col items-center md:items-start">
                <h2
                  className="cm-hero-heading text-[36px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white"
                  dangerouslySetInnerHTML={{
                    __html:
                      "AI Infrastructure and Market Data Intelligence for Capital Markets",
                  }}
                />
                <p className="cm-hero-copy text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                  Power LLM analytics, agentic workflows, and financial insight
                  generation using secure enterprise-grade data pipelines,
                  supported by real-time and historical market data
                  availability.
                </p>
              </div>
            </div>
            <div className="basis-[40%] flex justify-end">
              <img
                className="cm-hero-media w-full"
                src={Capital_Market_Hero}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section className="bg-[#010127] relative pb-[96px]" id="use-cases">
          <div className="cm-what-row container flex flex-col-reverse md:flex-row items-center mt-11 gap-8 md:gap-4 lg:gap-2 xl:gap-0">
            <div className="flex-1 basis-2/4w xl:basis-[40%] flex items-center justify-center">
              <img
                src={Capital_Deliver}
                alt="Data Structures"
                className="cm-what-media w-full md:max-w-[400px] h-auto"
              />
            </div>

            <div className="cm-what-copy flex-1 basis-2/4 xl:basis-[60%] space-y-4">
              <h2 class="text-2xl md:text-[32px] font-extrabold text-white text-center md:text-left">
                What is our Capital Markets Solution?
              </h2>

              <div class="space-y-2 mb-12">
                <p className="text-sm md:text-base text-white">
                  Our capital markets solution delivers both secure AI
                  infrastructure and direct access to market data, enabling
                  institutions to build reasoning-ready LLM workflows without
                  operational friction.
                </p>

                <p className="text-sm md:text-base text-white">
                  This solution combines:
                </p>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Market Data Access:
                    </span>
                    <span class="text-white ml-1">
                      Real-time and historical feeds available from partnered
                      data brokers and exchanges
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Secure Data Pipelines:
                    </span>
                    <span class="text-white ml-1">
                      Ingestion, transformation, normalization and governance of
                      market datasets
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">MCP Server:</span>
                    <span class="text-white ml-1">
                      Contextual enrichment using news, corporate actions and
                      domain signals for improved reasoning
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">PLM</span>
                    <span class="text-white ml-1">
                      Private models tailored to financial language and
                      analytical interpretation
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#EDF1FF] py-[48px]">
          <div className="container space-y-12">
            <h2 className="cm-why-head text-[#020128] font-extrabold text-[24px] md:text-[32px] text-center">
              Why choose us?
            </h2>

            <div className="cm-why-grid grid grid-cols-12 gap-8">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="cm-why-card group col-span-12 md:col-span-6 xl:col-span-3 bg-white hover:bg-poppingBlue flex flex-col items-center rounded-xl px-3 py-6 gap-4 transition-colors duration-700"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16 transition-all duration-700 group-hover:brightness-0 group-hover:invert"
                  />
                  <div className="space-y-1">
                    <h5 className="text-center text-base md:text-lg font-bold text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128] text-center transition-colors duration-700 group-hover:text-white">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#010127] relative py-[96px]" id="use-cases">
          <div className="cm-build-row container flex flex-col-reverse md:flex-row-reverse items-center  gap-8">
            <div className="flex-1 basis-2/4 flex items-center justify-center">
              <img
                src={Capital_Build}
                alt="Data Structures"
                className="cm-build-media w-full md:max-w-[400px] h-auto"
              />
            </div>

            <div className="cm-build-copy flex-1 basis-2/4 space-y-4">
              <h2 class="text-2xl md:text-[32px] font-extrabold text-white text-center md:text-left">
                Let's Build Your Applications
              </h2>

              <div class="space-y-2 mb-12">
                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Strategy Co‑Pilot:
                    </span>
                    <span class="text-white ml-1">
                      actor hypothesis drafting, structured backtest commentary,
                      regime classification
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Desk Intelligence:
                    </span>
                    <span class="text-white ml-1">
                      Auto‑generated trade reviews, playbooks and weekly
                      synthesis from desk artefacts
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          className="bg-[#010127] relative py-[116px]"
          style={{
            backgroundImage: `url("${Our_Product_Bg}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="cm-cta container flex flex-col items-center gap-6">
            <div className="cm-cta-item">
              <h2 className="text-[32px] md:text-[44px] text-center font-extrabold text-white">
                Built for Firms That Cannot Risk Exposure
              </h2>
              <p className="text-sm md:text-base text-center text-[#FFFFFF]">
                We enable AI in trading with control, explainability and
                sovereignty, keeping alpha inside the firewall.
              </p>
            </div>

            <Link className="cm-cta-item" to="/contact">
              <button className="text-sm md:text-base font-semibold py-3 w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                Talk to an Expert
              </button>
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}
